import http from "./http";

const AiDiagnosisService = {
  /**
   * Suggest ICD-10 codes using AI endpoint
   * @param {object} chartData - chart_data payload
   * @param {string} visitId - visit ID for logging
   * @param {string} organizationId - organization ID for logging
   * @returns {Promise<object>} suggestions response
   */
  async suggest(chartData, visitId = null, organizationId = null) {
    const payload = { 
      chart_data: chartData,
      visit_id: visitId,
      organization_id: organizationId,
    };
    const response = await http.POST("ai/diagnosis/suggest", payload);

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    // If backend returns error structure, surface message
    const message =
      response?.data?.message ||
      response?.message ||
      "Unable to fetch AI ICD suggestions";
    throw new Error(message);
  },

  /**
   * Get history data from progress notes for specific screen types
   * @param {string} organizationId - organization ID
   * @param {string} visitId - visit ID
   * @param {string[]} screenTypes - array of screen types (e.g. ['HPI'], ['HPI', 'PMH'], ['MUSCULOSKELETAL'])
   * @returns {Promise<object>} History data with screen_type as keys
   */
  async getHistoryData(organizationId, visitId, screenTypes = ['HPI']) {
    const screenTypesParam = screenTypes.map(type => `screen_types[]=${type}`).join('&');
    const response = await http.GET(
      `ai/diagnosis/history?organization_id=${organizationId}&visit_id=${visitId}&${screenTypesParam}`
    );

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    return null;
  },

  /**
   * Generate and store AI suggestions (call this when saving incident or EMR)
   * @param {object} chartData - chart_data payload
   * @param {string} visitId - visit ID
   * @param {string} organizationId - organization ID
   * @param {string} employeeId - employee ID (optional)
   * @returns {Promise<object>} suggestions response
   */
  async generateSuggestions(chartData, visitId, organizationId, employeeId = null) {
    const payload = { 
      chart_data: chartData,
      visit_id: visitId,
      organization_id: organizationId,
    };
    if (employeeId) {
      payload.employee_id = employeeId;
    }
    
    const response = await http.POST("ai/diagnosis/generate", payload);

    console.log("generateSuggestions response: ", response);
    if (response?.code === 200 && response.data) {
      return response.data;
    }

    const message =
      response?.data?.message ||
      response?.message ||
      "Unable to generate AI ICD suggestions";
    throw new Error(message);
  },

  /**
   * Generate and store AI suggestions by visit context only.
   * Backend builds chart_data from incident + saved EMR notes.
   *
   * @param {string} visitId - visit ID
   * @param {string} organizationId - organization ID
   * @param {string|null} employeeId - employee ID (optional)
   * @param {string} type - type of suggestions to generate ('assessment', 'hpi', or 'discharge')
   * @returns {Promise<object>} suggestions response
   */
  async generateSuggestionsByVisit(visitId, organizationId, employeeId = null, type = 'assessment') {
    const payload = {
      visit_id: visitId,
      organization_id: organizationId,
      type: type,
    };
    if (employeeId) {
      payload.employee_id = employeeId;
    }

    const response = await http.POST("ai/diagnosis/generate-by-visit", payload);

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    const message =
      response?.data?.message ||
      response?.message ||
      (type === 'discharge'
        ? "Unable to generate AI discharge suggestions"
        : type === 'hpi'
          ? "Unable to generate AI HPI suggestions"
          : "Unable to generate AI ICD suggestions");
    throw new Error(message);
  },

  /**
   * Get stored suggestions for a visit (from database, not calling API)
   * @param {string} visitId - visit ID
   * @param {string} type - type of suggestions ('assessment' or 'hpi')
   * @returns {Promise<object>} suggestions response
   */
  async getSuggestions(visitId, type = 'assessment') {
    const response = await http.GET(
      `ai/diagnosis/suggestions?visit_id=${visitId}&type=${type}`
    );

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    return { suggestions: [] };
  },

  /**
   * Accept a suggestion (mark as accepted and add to assessment)
   * @param {number} suggestionId - suggestion ID
   * @param {string} organizationId - organization ID
   * @param {string} visitId - visit ID
   * @returns {Promise<object>} response
   */
  async acceptSuggestion(suggestionId, organizationId, visitId) {
    const response = await http.POST("ai/diagnosis/accept", {
      suggestion_id: suggestionId,
      organization_id: organizationId,
      visit_id: visitId,
    });

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    const message =
      response?.data?.message ||
      response?.message ||
      "Unable to accept suggestion";
    throw new Error(message);
  },

  /**
   * Get HPI data from progress notes (backwards compatibility)
   * @deprecated Use getHistoryData() with screenTypes=['HPI'] instead
   * @param {string} organizationId - organization ID
   * @param {string} visitId - visit ID
   * @returns {Promise<object>} HPI data
   */
  async getHpiData(organizationId, visitId) {
    const response = await http.GET(
      `ai/diagnosis/hpi?organization_id=${organizationId}&visit_id=${visitId}`
    );

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    return null;
  },

  /**
   * Get AI diagnosis logs with pagination and filtering
   * @param {object} params - Query parameters (page, per_page, visit_id, organization_id, status, search_query)
   * @returns {Promise<object>} response
   */
  async getLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const response = await http.GET(`ai/diagnosis/logs?${queryString}`);

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    const message =
      response?.data?.message ||
      response?.message ||
      "Unable to fetch logs";
    throw new Error(message);
  },

  /**
   * Get a single log entry by ID
   * @param {string} logId - Log ID
   * @returns {Promise<object>} response
   */
  async getLogById(logId) {
    const response = await http.GET(`ai/diagnosis/logs/${logId}`);

    if (response?.code === 200 && response.data) {
      return response.data;
    }

    const message =
      response?.data?.message ||
      response?.message ||
      "Unable to fetch log";
    throw new Error(message);
  },
};

export default AiDiagnosisService;


